import { memo, useState, useCallback, useEffect, useContext, useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { rem } from 'polished'
import Calendar from 'components/Common/Calendar'
import {
  addMonths,
  endOfMonth,
  dateIsAfter,
  dateIsBefore,
  startOfMonth,
  useIntlDateFormatter,
} from 'lib/datetime/dateUtils'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import {
  INTL_LONG_MONTH_LONG_YEAR,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import Heading from '../../../../LuxKit/Typography/Heading'
import LineAngleLeftBIcon from 'LuxKit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import useDidChange from 'hooks/useDidChange'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import IconButton from 'LuxKit/Button/IconButton'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import {
  type CalendarFocusMonth,
  type CalendarVisibleMonths,
  toCalendarFocusDate,
  toCalendarMonthKey,
} from './calendarFocusMonth'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import CalendarEventMonthPreview from './CalendarEventMonthPreview'

const Root = styled.div`
  display: flex;
  width: 100%;

  > * {
    flex-grow: 1;

    & + * {
      margin-inline-start: ${rem(52)};
    }
  }
`

const Header = styled.div`
  display: grid;
  /* The preview columns collapse to zero width when no event is nearby. */
  grid-template:
    "prev-button prev-preview heading next-preview next-button" auto
    / ${rem(28)} auto 1fr auto ${rem(28)};
  place-items: center;
  column-gap: ${rem(4)};
`

const HeaderHeading = styled(Heading)`
  grid-area: heading;
`

const LeftArrow = styled(IconButton)`
  grid-area: prev-button;
`

const RightArrow = styled(IconButton)`
  grid-area: next-button;
`

const PrevPreview = styled(CalendarEventMonthPreview)`
  grid-area: prev-preview;
`

const NextPreview = styled(CalendarEventMonthPreview)`
  grid-area: next-preview;
`

interface CalendarMonthPreview {
  event: Pick<SearchCalendarEvent, 'id' | 'icon' | 'color'>
  /** Event name, shown on hover and announced as the chip's label. */
  label: string
  onHover?: (eventId: string) => void
}

interface Props {
  onDatesChange: (dates: { startDate: moment.Moment, endDate: moment.Moment | undefined }) => void
  endDate?: moment.Moment
  startDate?: moment.Moment
  /** Initial viewport month only — not used for late-arriving focus sync. */
  initDate?: moment.Moment
  /**
   * Month the dual panels should open on. Purely a viewport hint — it never
   * selects a date. Prefer this over `initDate` when the hint can arrive or
   * change after mount.
   */
  focusMonth?: CalendarFocusMonth
  minDate?: Date
  maxDate?: Date
  className?: string
  startLabel?: string
  endLabel?: string
  availableMonths?: Array<string>
  allowSameDayRange?: boolean
  overlayEvents?: ReadonlyArray<Pick<SearchCalendarEvent, 'id' | 'startDate' | 'endDate' | 'color' | 'icon'>>
  /** Fires on mount and whenever the two visible months change. */
  onVisibleMonths?: (months: CalendarVisibleMonths) => void
  /**
   * Event hints for the months paging would newly reveal — one step back and one
   * step forward. Rendered beside the matching chevron, and only while that chevron
   * can actually be used.
   */
  previousMonthPreview?: CalendarMonthPreview
  nextMonthPreview?: CalendarMonthPreview
}

function DualDateRangePicker(props: Props) {
  const {
    onDatesChange,
    endDate,
    startDate,
    initDate,
    focusMonth,
    minDate,
    maxDate,
    className,
    startLabel,
    endLabel,
    availableMonths,
    allowSameDayRange,
    overlayEvents,
    onVisibleMonths,
    previousMonthPreview,
    nextMonthPreview,
  } = props
  const [index, setIndex] = useState<number>(0)
  const [date, setDate] = useState<Date>(() => {
    const focusDate = toCalendarFocusDate(focusMonth)
    return focusDate ?? initDate?.toDate() ?? startDate?.toDate() ?? new Date()
  })
  const focusTime = toCalendarFocusDate(focusMonth)?.getTime()
  const dateFormatter = useIntlDateFormatter()

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDayClick = useCallback((day: moment.Moment) => {
    const isSameStartDay = startDate && day.isSame(startDate, 'day')
    const shouldSelectEndDate = startDate && !endDate && (day.isAfter(startDate) || (allowSameDayRange && isSameStartDay))

    if (shouldSelectEndDate) {
      onDatesChange({ startDate, endDate: day })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckOut, moment(day).format(ISO_DATE_FORMAT))
    } else {
      onDatesChange({ startDate: day, endDate: undefined })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CheckIn, moment(day).format(ISO_DATE_FORMAT))
    }
  }, [allowSameDayRange, startDate, endDate, onDatesChange, onUserAnalyticsAction])

  const onNextMonth = useCallback(() => {
    if (availableMonths) {
      if (index >= availableMonths.length - 1) return
      setIndex(index + 1)
    }
    setDate(addMonths(date, 1))
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.ShiftMonth, CALENDAR_GA_EVENT_LABEL.NextMonth)
  }, [availableMonths, date, index, onUserAnalyticsAction])

  const onPrevMonth = useCallback(() => {
    if (availableMonths) {
      if (!index) return
      setIndex(index - 1)
    }
    setDate(addMonths(date, -1))
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.ShiftMonth, CALENDAR_GA_EVENT_LABEL.PreviousMonth)
  }, [availableMonths, date, index, onUserAnalyticsAction])

  const currentDate = useMemo(() => {
    if (availableMonths?.[index]) {
      return moment(availableMonths[index], 'YYYY-MM').toDate()
    }
    return date
  }, [availableMonths, date, index])

  const nextDate = useMemo(() => {
    if (availableMonths?.length) {
      return index < availableMonths.length - 1 ? moment(availableMonths[index + 1], 'YYYY-MM').toDate() : undefined
    }

    return addMonths(date, 1)
  }, [availableMonths, date, index])

  const formattedDate = useCallback((date: Date) => {
    const momentDate = moment(date)
    if (!momentDate.isValid()) return ''
    return dateFormatter(momentDate, INTL_LONG_MONTH_LONG_YEAR)
  }, [dateFormatter])

  useEffect(() => {
    if (startDate || focusTime === undefined) return

    const nextDate = new Date(focusTime)
    setDate((currentDate) => (
      currentDate.getFullYear() === nextDate.getFullYear() &&
      currentDate.getMonth() === nextDate.getMonth()
        ? currentDate
        : nextDate
    ))

    if (availableMonths?.length) {
      const nextIndex = availableMonths.findIndex(monthKey =>
        moment(monthKey, 'YYYY-MM').isSame(nextDate, 'month'),
      )
      if (nextIndex >= 0) setIndex(nextIndex)
    }
  }, [availableMonths, focusTime, startDate])

  const startDateChanged = useDidChange(startDate)
  useEffect(() => {
    if (startDate && startDateChanged) {
      const isOnLeftPanel = startDate.isSame(date, 'month')
      const isOnRightPanel = startDate.isSame(addMonths(date, 1), 'month')
      if (!isOnLeftPanel && !isOnRightPanel) {
        setDate(startDate.toDate())
      }
    }
  }, [date, startDate, startDateChanged])

  useEffect(() => {
    onVisibleMonths?.({
      left: toCalendarMonthKey(currentDate),
      right: nextDate ? toCalendarMonthKey(nextDate) : undefined,
    })
  }, [currentDate, nextDate, onVisibleMonths])

  const isPrevDisabled = dateIsBefore(startOfMonth(currentDate), minDate)
  const isNextDisabled = !nextDate || dateIsAfter(endOfMonth(nextDate))

  return (<Root className={className}>
    <VerticalSpacer gap={8}>
      <Header>
        <LeftArrow
          kind="tertiary"
          size="small"
          shape="circle"
          horizontalOutdent="start"
          disabled={isPrevDisabled}
          onClick={onPrevMonth}
        >
          <LineAngleLeftBIcon />
        </LeftArrow>
        {!!previousMonthPreview && !isPrevDisabled && (
          <PrevPreview
            event={previousMonthPreview.event}
            label={previousMonthPreview.label}
            onHover={previousMonthPreview.onHover}
          />
        )}
        <HeaderHeading variant="heading6" align="center">
          {formattedDate(currentDate)}
        </HeaderHeading>
      </Header>
      <Calendar
        month={currentDate.getMonth()}
        year={currentDate.getFullYear()}
        startDate={startDate?.toDate()}
        minDate={minDate}
        maxDate={maxDate}
        endDate={endDate?.toDate()}
        onDayClick={onDayClick}
        startLabel={startLabel}
        endLabel={endLabel}
        overlayEvents={overlayEvents}
      />
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Header>
        {!!nextDate && <HeaderHeading variant="heading6" align="center">
          {formattedDate(nextDate)}
        </HeaderHeading>}
        {!!nextMonthPreview && !isNextDisabled && (
          <NextPreview
            event={nextMonthPreview.event}
            label={nextMonthPreview.label}
            onHover={nextMonthPreview.onHover}
          />
        )}
        {!!nextDate && <RightArrow
          kind="tertiary"
          size="small"
          shape="circle"
          horizontalOutdent="end"
          data-testid="next-month-calendar"
          disabled={isNextDisabled}
          onClick={onNextMonth}
        >
          <LineAngleRightBIcon />
        </RightArrow>}
      </Header>
      {!!nextDate && <Calendar
        month={nextDate.getMonth()}
        year={nextDate.getFullYear()}
        startDate={startDate?.toDate()}
        minDate={minDate}
        maxDate={maxDate}
        endDate={endDate?.toDate()}
        onDayClick={onDayClick}
        startLabel={startLabel}
        endLabel={endLabel}
        overlayEvents={overlayEvents}
      />}
    </VerticalSpacer>
  </Root>
  )
}

export default memo(DualDateRangePicker)
